import { useState, useEffect, useCallback } from "react";
import { MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weather without surveilling kids: NO geolocation prompt on load and NO
// third-party geocoder called from the browser. The backend serves weather
// for a deployment-configured default city; coordinates are only sent after
// an explicit "my area" tap (browser permission flow), and never stored.
export const WeatherBar = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [usedLocation, setUsedLocation] = useState(false);

  const fetchDefaultWeather = useCallback(async () => {
    try {
      const res = await fetch(`${API}/weather`);
      if (!res.ok) throw new Error("Weather API failed");
      setWeather(await res.json());
    } catch {
      setWeather(null); // render nothing rather than a broken bar
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDefaultWeather();
  }, [fetchDefaultWeather]);

  const useMyLocation = () => {
    if (!navigator.geolocation || locating) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `${API}/weather?lat=${latitude}&lon=${longitude}&city=${encodeURIComponent("your area")}`
          );
          if (res.ok) {
            setWeather(await res.json());
            setUsedLocation(true);
          }
        } catch {
          // keep the default-city weather
        }
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 5000, enableHighAccuracy: false }
    );
  };

  if (loading || !weather) return null;

  return (
    <div className="py-3 px-5 md:px-10" data-testid="weather-bar">
      {/* Pill container with amber accent per design_guidelines.json */}
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 md:gap-4 bg-white border border-scoop-border border-l-4 border-l-scoop-amber rounded-full px-4 md:px-5 py-2.5">
          <span className="text-xl flex-shrink-0" aria-hidden="true">🌦️</span>

          <p className="text-sm font-medium text-scoop-ink flex-1 min-w-0 truncate">
            <strong className="font-bold">
              Weather in {usedLocation ? "your area" : weather.city}:
            </strong>{" "}
            {weather.message}
          </p>

          {weather.chips && weather.chips.length > 0 && (
            <div className="hidden md:flex gap-2 flex-shrink-0">
              {weather.chips.map((chip, index) => (
                <span
                  key={index}
                  className="weather-chip text-[0.7rem] font-semibold text-scoop-ink bg-scoop-amber/10 border border-scoop-amber/30 px-3 py-1 rounded-full"
                >
                  {chip.icon} {chip.label}
                </span>
              ))}
            </div>
          )}

          {!usedLocation && (
            <button
              onClick={useMyLocation}
              disabled={locating}
              className="flex-shrink-0 inline-flex items-center gap-1 text-[0.7rem] font-semibold text-scoop-blue hover:underline disabled:opacity-50"
              aria-label="Show weather for my area"
              data-testid="weather-use-location"
            >
              <MapPin className="w-3 h-3" />
              {locating ? "Finding…" : "My area"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeatherBar;
